import { StargraveStat, StargraveStatLine, StargraveTechnology, StargraveTechnologyInstance } from '../types';
import { stargraveAdvancedTechnology } from '../data/stargrave/advancedTechnology';
import { StatModifier } from '../utils/splitStats';

const STATS: StargraveStat[] = ['move', 'fight', 'shoot', 'armour', 'will', 'health'];

/** Technology items currently assigned to the member. */
export function getOwnedStargraveTechnology(
  instances: StargraveTechnologyInstance[],
  memberId: string
): StargraveTechnology[] {
  return instances
    .filter((instance) => instance.ownerId === memberId)
    .flatMap((instance) => {
      const technology = stargraveAdvancedTechnology.find((candidate) => candidate.id === instance.technologyId);
      return technology ? [technology] : [];
    });
}

export function getStargraveTechnologyStatModifiers(
  instances: StargraveTechnologyInstance[],
  memberId: string,
  stat: StargraveStat
): StatModifier[] {
  return getOwnedStargraveTechnology(instances, memberId).flatMap((technology) => {
    const value = technology.statModifiers?.[stat];
    return value === undefined ? [] : [{ label: technology.name, value, type: 'add' as const }];
  });
}

/** Applies passive stat bonuses from assigned technology; caps limit only their own item's gain. */
export function applyStargraveTechnologyModifiers(
  baseStats: StargraveStatLine,
  instances: StargraveTechnologyInstance[],
  memberId: string
): StargraveStatLine {
  const owned = getOwnedStargraveTechnology(instances, memberId);
  if (owned.length === 0) return baseStats;
  const stats = { ...baseStats };
  for (const stat of STATS) {
    const contributions = owned.flatMap((technology) => {
      const value = technology.statModifiers?.[stat];
      return value === undefined ? [] : [{ delta: value, cap: technology.statMaximums?.[stat] }];
    });
    let value = stats[stat];
    for (const { delta, cap } of contributions) {
      if (cap === undefined) value += delta;
    }
    for (const { delta, cap } of contributions) {
      if (cap !== undefined) value = Math.min(value + delta, Math.max(value, cap));
    }
    stats[stat] = value;
  }
  return stats;
}

export interface StargraveTechnologyEffect {
  id: string;
  name: string;
  description?: string;
  modifiers?: string;
}

/** Conditional/non-stat effects shown as chips while the item is assigned. */
export function getStargraveTechnologyEffects(
  instances: StargraveTechnologyInstance[],
  memberId: string
): StargraveTechnologyEffect[] {
  return getOwnedStargraveTechnology(instances, memberId).flatMap((technology) =>
    technology.conditionalEffect
      ? [{ id: `technology-${technology.id}`, name: technology.name, description: technology.summary, modifiers: technology.conditionalEffect }]
      : []
  );
}
